/**
 * PanelDeIndices.tsx — los seis índices operativos contra su meta.
 *
 * Es la tercera cláusula del hito de E5. Los números salen del libro: nadie los
 * teclea, y por eso el panel puede decir de dónde vienen —numerador y
 * denominador— sin inventar nada.
 *
 * ── ▶ LA TRAMPA DEL COLOR, QUE ES LA DECISIÓN DE ESTE ARCHIVO ─────────────
 *
 * Hay dos clases de medida y **se leen en direcciones opuestas**:
 *
 *   `indice`           NR ÷ OPEX del área.  **Más alto es mejor.**
 *   `porcentaje_de_nr` gasto del área sobre el NR. **Más bajo es mejor.**
 *
 * Pintarlas igual diría verde cuando la cosa va mal. Un I+D al 35 % contra una
 * meta del 20 % está por ENCIMA de la meta y es una alerta, no un logro.
 *
 * ── Provisional se dice, no se insinúa ────────────────────────────────────
 *
 * Mientras el mes no cierra, el índice se calcula sobre el acumulado y **no es
 * definitivo**. El modelo contable lo pide con esas palabras. Un número sin esa
 * marca acaba en una presentación.
 *
 * ── Y lo que no se pudo calcular dice por qué ─────────────────────────────
 *
 * Ni un cero ni un guion suelto: el motivo. «0,00» se lee como que no hubo
 * mantenimiento; el motivo dice que falta una dimensión en el catálogo.
 */
import { useEffect, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { descargarArchivo } from '../nucleo/descargar.ts';
import { useSesion } from '../nucleo/sesion.tsx';
import {
  descargarEstado, traerIndices, FalloDeApi, type IndiceReal,
} from '../datos/libros.ts';

/**
 * Si la medida está donde debería.
 *
 * `null` cuando no hay meta fijada — que no es lo mismo que estar lejos de
 * ella. El índice de suscripciones está así por decisión del modelo: se fija
 * con dos trimestres de datos reales.
 */
function cumple(i: IndiceReal): boolean | null {
  if (i.valor === null || i.meta === null) return null;
  const valor = Number(i.valor);
  const meta = Number(i.meta);
  if (!Number.isFinite(valor) || !Number.isFinite(meta)) return null;
  return i.tipo === 'porcentaje_de_nr' ? valor <= meta : valor >= meta;
}

function Medida({ indice, moneda }: { indice: IndiceReal; moneda: string }) {
  const { t, idioma, monto } = useIdioma();
  const nombre = idioma === 'en' ? indice.nombre_en : indice.nombre;
  const ok = cumple(indice);

  return (
    <div className="indice">
      <div className="indice__cab">
        <span className="indice__nombre">{nombre}</span>
        {indice.provisional && (
          <span className="chip chip--warn" title={t('idx.provisionalWhy')}>
            {t('idx.provisional')}
          </span>
        )}
      </div>

      {indice.estado === 'sin_datos' ? (
        // El motivo, no un guion. Un guion obliga a preguntar y nadie pregunta.
        // Viene como clave desde la base: la frase es de este diccionario, y
        // existe en los dos idiomas.
        <p className="indice__sinDatos">
          {indice.motivo ? t(`idx.motivo.${indice.motivo}` as never) : t('idx.noData')}
        </p>
      ) : (
        <>
          <div className="indice__valor">
            <span className={ok === false ? 'is-warn' : ok === true ? 'is-ok' : ''}>
              {indice.valor}
              {indice.tipo === 'porcentaje_de_nr' ? ' %' : ''}
            </span>
            <span className="indice__meta">
              {indice.meta === null
                ? t('idx.noTarget')
                : `${t('idx.target')} ${indice.meta}${indice.tipo === 'porcentaje_de_nr' ? ' %' : ''}`}
              {/* ▶▶ Y SI NADIE DE ESTA EMPRESA LA MIRÓ, SE DICE.
                  Era el hallazgo del §13 de la revisión: el tablero enseñaba
                  «meta 12 %» como si fuera de la empresa, y era una sola fila
                  para todas. Guardarla aparte no habría servido de nada si la
                  pantalla la seguía presentando como una decisión tomada. */}
              {indice.meta_confirmada === false && (
                <> <span className="chip chip--soft" title={t('idx.targetProposedWhy')}>
                  {t('idx.targetProposed')}
                </span></>
              )}
            </span>
          </div>
          {/* De dónde salió. Un indicador sin sus dos términos no se puede
              discutir, y el que no se puede discutir no se usa. */}
          {/* Con formato: son dos importes, y salían crudos —«20291525.00 /
              99764000.00»— en la única pantalla donde todo lo demás pasa por
              `monto()`. Un número de ocho cifras sin separadores no se lee: se
              cuenta con el dedo.
              Y solo si están los DOS: media razón no se puede discutir. */}
          {indice.numerador !== null && indice.denominador !== null && (
            <p className="indice__origen mono">
              {monto({ valor: indice.numerador, moneda })}
              {' / '}
              {monto({ valor: indice.denominador, moneda })}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function PanelDeIndices({ anio, mes }: { anio: number; mes: number }) {
  const { t } = useIdioma();
  const { pedir, sesion } = useSesion();
  const [indices, setIndices] = useState<readonly IndiceReal[] | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [bajando, setBajando] = useState(false);

  const empresaId = sesion?.empresa.id ?? null;

  useEffect(() => {
    if (!empresaId) return;
    let vivo = true;
    setIndices(null);
    setFallo(null);
    (async () => {
      try {
        const i = await traerIndices(pedir, anio, mes);
        if (vivo) setIndices(i);
      } catch (e) {
        if (!vivo) return;
        const f = e instanceof FalloDeApi ? e : null;
        setFallo(f?.clase === 'conexion' ? 'sb.conexion'
          : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
      }
    })();
    return () => { vivo = false; };
  }, [pedir, empresaId, anio, mes]);

  /**
   * ▶ Exportar los índices — la tercera cláusula de E7.
   *
   * El hito pide compararlos contra la hoja de cálculo que SkopedLabs lleva
   * hoy, y una comparación se hace con dos columnas al lado. Un tablero que no
   * se puede exportar obliga a teclear los números otra vez para compararlos,
   * y ahí aparecen diferencias que no existían.
   *
   * El CSV trae `numerador` y `denominador`, que en pantalla no se ven: que
   * dos resultados no cuadren no dice nada; ver cuál de los dos lados difiere,
   * sí.
   *
   * ▶ EXPORTAR ES LEER: este botón NO cuelga de la capacidad de escritura. Una
   * empresa suspendida cae al piso —lee y exporta, no registra— y encerrarlo
   * detrás de `puedeRegistrar` le quitaría justo lo que el piso promete. Lo
   * encontró el guardián 7 en su primera corrida: este archivo era el cuarto
   * que exporta y el único que no lo decía.
   */
  const exportar = async () => {
    setBajando(true);
    setFallo(null);
    try {
      const { nombre, blob } = await descargarEstado(pedir, 'indices', anio, mes);
      descargarArchivo(nombre, blob);
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    } finally {
      setBajando(false);
    }
  };

  return (
    <section className="card">
      <div className="card__head">
        <h3>{t('idx.title')}</h3>
        <span className="spacer" />
        <span className="small muted">{t('idx.fromLedger')}</span>
        <button
          className="btn btn--ghost btn--sm" type="button"
          disabled={bajando || indices === null}
          onClick={() => void exportar()}
        >
          {t('common.export')}
        </button>
      </div>

      {fallo && <p className="muted">{t(fallo as never)}</p>}
      {indices === null && !fallo && <p className="muted">{t('common.loading')}</p>}

      {indices && (
        <div className="indices">
          {indices.map((i) => (
            <Medida key={i.clave} indice={i} moneda={sesion?.empresa.monedaLocal ?? ''} />
          ))}
        </div>
      )}
    </section>
  );
}
